import { Button, Spin, Upload } from 'antd';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { IMAGE_ACCEPT, validateImageFile } from '@/shared/lib/image-file';
import { useObjectUrl } from '@/shared/lib/hooks/useObjectUrl';
import { ImageThumb } from './ImageThumb';

export interface ImagePickerProps {
  /** Выбранный, но ещё не отправленный файл (режим создания). В режиме правки — `null`. */
  file: File | null;
  /** `File` — выбран годный файл; `null` — выбор отменён кнопкой «×». */
  onChange: (file: File | null) => void;
  /** Уже сохранённое фото: показывается, пока нет выбранного файла. */
  currentUrl?: string | null;
  /** Сторона в пикселях: 96 для окна категории, 24 для ячейки таблицы. */
  size?: number;
  disabled?: boolean;
  /** Идёт отправка: поверх фото крутится индикатор, выбор отключён. */
  loading?: boolean;
  /** Файл не прошёл проверку типа/размера — сказать пользователю. */
  onReject?: (reason: string) => void;
  title?: string;
}

/** Выбор фото (§2.1): клик по квадрату открывает диалог файла, выбранное показывается превью.
 *  Сам ничего не отправляет — отправка за вызывающим (после создания записи или сразу при правке). */
export function ImagePicker({ file, onChange, currentUrl, size = 96, disabled, loading, onReject, title = 'Выбрать фото' }: ImagePickerProps) {
  const localUrl = useObjectUrl(file);
  const src = localUrl ?? currentUrl ?? null;
  const locked = disabled || loading;

  function beforeUpload(picked: File): false {
    const reason = validateImageFile(picked);
    if (reason) onReject?.(reason);
    else onChange(picked);
    return false; // отправку делает вызывающий; antd Upload сам ничего не шлёт
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {/* fileList пустой всегда: antd Upload не копит выбранные файлы у себя */}
      <Upload accept={IMAGE_ACCEPT} showUploadList={false} fileList={[]} beforeUpload={beforeUpload} disabled={locked}>
        <button
          type="button"
          title={title}
          aria-label={title}
          disabled={locked}
          style={{
            position: 'relative',
            padding: 0,
            width: size,
            height: size,
            border: src ? 'none' : '1px dashed #bfbfbf',
            background: 'transparent',
            cursor: locked ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8c8c8c',
          }}
        >
          {src ? <ImageThumb src={src} size={size} /> : <PlusOutlined />}
          {loading ? (
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
              <Spin size="small" />
            </span>
          ) : null}
        </button>
      </Upload>
      {file && !loading ? <Button type="text" size="small" aria-label="Убрать выбранное фото" icon={<CloseOutlined />} onClick={() => onChange(null)} /> : null}
    </span>
  );
}
